"use client";

import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import { Code, ChevronDown, X, GripVertical, ImagePlus } from "lucide-react";
import { cn } from "../lib/utils";
import { codeToHtml } from "shiki";
import {
  createBlockEditorValueSyncState,
  receiveBlockEditorValue,
  recordBlockEditorValueEmission,
} from "../lib/block-editor-value-sync";

/** 가장 가까운 스크롤 가능한 부모 요소 찾기 */
function findScrollableParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/** 스크롤 위치를 보존하면서 textarea 높이 재계산 (rAF로 forced reflow 최소화) */
function resizeTextarea(el: HTMLTextAreaElement) {
  requestAnimationFrame(() => {
    const scrollParent = findScrollableParent(el);
    const savedScrollTop = scrollParent?.scrollTop;

    el.style.height = "0";
    el.style.height = el.scrollHeight + "px";

    if (scrollParent && savedScrollTop !== undefined) {
      scrollParent.scrollTop = savedScrollTop;
    }
  });
}

const CODE_LANGUAGES = [
  { label: "C언어", value: "c" },
  { label: "Java", value: "java" },
  { label: "Python", value: "python" },
  { label: "SQL", value: "sql" },
] as const;

const LANGUAGE_LABELS: Record<string, string> = {
  c: "C",
  java: "Java",
  python: "Python",
  sql: "SQL",
};

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "허용되지 않는 파일 형식입니다. (JPEG, PNG, GIF, WebP만 가능)";
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return "파일 크기는 5MB 이하여야 합니다.";
  }
  return null;
}

interface EditorBlock {
  id: string;
  type: "text" | "code" | "image";
  content: string;
  language?: string;
  file?: File;
  previewUrl?: string;
  imageWidth?: number;
}

let blockIdCounter = 0;
function genId() {
  return `blk-${++blockIdCounter}-${Date.now()}`;
}

function parseBlocks(content: string): EditorBlock[] {
  if (!content) return [{ id: genId(), type: "text", content: "" }];

  const blocks: EditorBlock[] = [];
  const lines = content.split("\n");
  let textLines: string[] = [];
  let inCode = false;
  let codeLang = "";
  let codeLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    // 마크다운 이미지 ![alt](url)
    if (!inCode) {
      const imageMatch = trimmed.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      if (imageMatch) {
        if (textLines.length > 0 || blocks.length === 0) {
          blocks.push({ id: genId(), type: "text", content: textLines.join("\n") });
          textLines = [];
        }
        const imgAlt = imageMatch[1];
        const imgUrl = imageMatch[2];
        const widthMatch = imgAlt.match(/\|(\d+)$/);
        blocks.push({
          id: genId(),
          type: "image",
          content: imgUrl,
          previewUrl: imgUrl,
          imageWidth: widthMatch ? parseInt(widthMatch[1]) : undefined,
        });
        continue;
      }
    }

    if (!inCode && trimmed.startsWith("```")) {
      // 현재까지의 텍스트를 블록으로 추가
      blocks.push({ id: genId(), type: "text", content: textLines.join("\n") });
      textLines = [];
      inCode = true;
      codeLang = trimmed.slice(3).trim();
      codeLines = [];
    } else if (inCode && trimmed === "```") {
      blocks.push({
        id: genId(),
        type: "code",
        content: codeLines.join("\n"),
        language: codeLang || "text",
      });
      inCode = false;
      codeLang = "";
      codeLines = [];
    } else if (inCode) {
      codeLines.push(line);
    } else {
      textLines.push(line);
    }
  }

  // 닫히지 않은 코드 블록은 텍스트로 처리
  if (inCode) {
    textLines.push("```" + codeLang);
    textLines.push(...codeLines);
  }

  if (textLines.length > 0 || blocks.length === 0) {
    blocks.push({ id: genId(), type: "text", content: textLines.join("\n") });
  }

  return blocks;
}

function blocksToString(blocks: EditorBlock[]): string {
  return blocks
    .map((b) => {
      if (b.type === "code") return `\`\`\`${b.language || "text"}\n${b.content}\n\`\`\``;
      if (b.type === "image") {
        const alt = b.imageWidth ? `image|${b.imageWidth}` : "image";
        return `![${alt}](${b.previewUrl || b.content})`;
      }
      return b.content;
    })
    .filter((s) => s.trim() !== "")
    .join("\n");
}

/** 자동 높이 조절 textarea */
function AutoResizeTextarea({
  value,
  onChange,
  placeholder,
  className,
  onKeyDown,
  onFocusBlock,
  onCursorChange,
  readOnly = false,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocusBlock?: () => void;
  onCursorChange?: (pos: number) => void;
  readOnly?: boolean;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (ref.current) {
      resizeTextarea(ref.current);
    }
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      readOnly={readOnly}
      aria-label={placeholder || "텍스트 블록 내용"}
      onChange={(e) => {
        if (readOnly) return;
        onChange(e.target.value);
        onCursorChange?.(e.target.selectionStart);
      }}
      onKeyDown={onKeyDown}
      onFocus={() => {
        onFocusBlock?.();
        if (ref.current) onCursorChange?.(ref.current.selectionStart);
      }}
      onSelect={() => {
        if (ref.current) onCursorChange?.(ref.current.selectionStart);
      }}
      placeholder={placeholder}
      autoComplete="off"
      data-text-block
      data-1p-ignore
      data-lpignore="true"
      className={cn(
        "w-full resize-none border-none outline-none bg-transparent text-sm leading-relaxed",
        "min-h-[1.5em]",
        "overflow-hidden",
        "text-[#333] dark:text-neutral-100",
        "placeholder:text-gray-400 dark:placeholder:text-neutral-500",
        className
      )}
      rows={1}
    />
  );
}

/** 코드 블록 에디터 (Shiki 구문 강조 오버레이) */
function CodeBlockEdit({
  value,
  language,
  onChange,
  onRemove,
  onDragStart,
  onDragEnd,
  isDragging,
  readOnly = false,
}: {
  value: string;
  language: string;
  onChange: (val: string) => void;
  onRemove: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  readOnly?: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [highlightedHtml, setHighlightedHtml] = useState("");

  // 자동 높이 조절
  useEffect(() => {
    if (textareaRef.current) {
      resizeTextarea(textareaRef.current);
    }
  }, [value]);

  // 디바운스된 Shiki 구문 강조 — 외부 비동기 highlight 동기화
  useEffect(() => {
    if (!value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHighlightedHtml("");
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const html = await codeToHtml(value, {
          lang: language === "text" ? "txt" : language,
          themes: {
            light: "github-light",
            dark: "github-dark",
          },
        });
        setHighlightedHtml(html);
      } catch {
        setHighlightedHtml("");
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [value, language]);

  // 새로 생성된 코드 블록에 자동 포커스
  useEffect(() => {
    if (textareaRef.current && !value && !readOnly) {
      textareaRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codeStyle: React.CSSProperties = {
    fontFamily: "var(--font-geist-mono, ui-monospace, SFMono-Regular, monospace)",
    fontSize: "13px",
    lineHeight: "1.6",
    tabSize: 2,
  };

  return (
    <div
      className={cn(
        "rounded-lg bg-[#f6f8fa] dark:bg-[#0d1117] overflow-hidden border border-gray-200 dark:border-[#30363d] transition-opacity",
        isDragging && "opacity-50"
      )}
    >
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#eceaea] dark:bg-[#161b22] border-b border-gray-200 dark:border-[#21262d]">
        <div className="flex items-center gap-1.5">
          <div
            draggable={!readOnly}
            onDragStart={(e) => {
              if (readOnly) {
                e.preventDefault();
                return;
              }
              e.dataTransfer.effectAllowed = "move";
              onDragStart?.();
            }}
            onDragEnd={() => onDragEnd?.()}
            className={cn("text-gray-400 dark:text-gray-500 transition-colors", readOnly ? "cursor-not-allowed opacity-50" : "cursor-grab active:cursor-grabbing hover:text-gray-600 dark:hover:text-gray-300")}
            title="드래그하여 이동"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </div>
          <span className="text-[11px] text-[#706E6E] dark:text-gray-400 font-medium tracking-wide">
            {LANGUAGE_LABELS[language] || language}
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={readOnly}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          title="코드 블록 삭제"
          aria-label="코드 블록 삭제"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* 코드 편집 영역: textarea 위에 구문 강조 오버레이 */}
      <div className="relative">
        {/* 구문 강조 레이어 (배경) */}
        {highlightedHtml && (
          <div
            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
            className={cn(
              "absolute inset-0 pointer-events-none overflow-hidden",
              "[&>pre]:!bg-transparent [&>pre]:!m-0 [&>pre]:!p-0 [&>pre]:!border-0",
              "[&>pre]:whitespace-pre-wrap [&>pre]:break-words",
              "[&>pre>code]:!bg-transparent [&>pre>code]:!p-0"
            )}
            style={{ ...codeStyle, padding: "12px 16px" }}
            aria-hidden="true"
          />
        )}
        {/* 편집 가능한 textarea (전경, 텍스트 투명) */}
        <textarea
          ref={textareaRef}
          value={value}
          readOnly={readOnly}
          aria-label="코드 블록 내용"
          onChange={(e) => {
            if (!readOnly) onChange(e.target.value);
          }}
          placeholder="코드를 입력하세요..."
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          className={cn(
            "w-full resize-none border-none outline-none bg-transparent relative z-10",
            "px-4 py-3 min-h-[2.5em] overflow-hidden whitespace-pre-wrap break-words",
            highlightedHtml
              ? "text-transparent caret-black dark:caret-white selection:bg-blue-500/30"
              : "text-[#24292e] dark:text-[#e6edf3]",
            "placeholder:text-gray-400 dark:placeholder:text-[#484f58]"
          )}
          style={codeStyle}
          rows={1}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

/** 이미지 블록 에디터 (리사이즈 지원) */
function ImageBlockEdit({
  previewUrl,
  imageWidth,
  onWidthChange,
  onRemove,
  onDragStart,
  onDragEnd,
  isDragging,
  readOnly = false,
}: {
  previewUrl: string;
  imageWidth?: number;
  onWidthChange?: (width: number | undefined) => void;
  onRemove: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  readOnly?: boolean;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [resizing, setResizing] = useState(false);
  const [displayWidth, setDisplayWidth] = useState<number | undefined>(imageWidth);
  const startRef = useRef({ x: 0, width: 0 });
  const [previousReadOnly, setPreviousReadOnly] = useState(readOnly);
  if (previousReadOnly !== readOnly) {
    setPreviousReadOnly(readOnly);
    if (readOnly) setResizing(false);
  }

  // 외부 imageWidth 변경 시 동기화 (렌더 시점 prev-prop 패턴, JCG-2035)
  const [prevImageWidth, setPrevImageWidth] = useState(imageWidth);
  if (prevImageWidth !== imageWidth) {
    setPrevImageWidth(imageWidth);
    if (!resizing) setDisplayWidth(imageWidth);
  }

  useEffect(() => {
    if (!resizing || readOnly) return;

    const onMove = (e: MouseEvent) => {
      const delta = e.clientX - startRef.current.x;
      const w = Math.max(60, Math.min(500, startRef.current.width + delta));
      setDisplayWidth(w);
    };

    const onUp = () => {
      setResizing(false);
      onWidthChange?.(displayWidth);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [resizing, displayWidth, onWidthChange, readOnly]);

  const handleResizeStart = (e: React.MouseEvent) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setResizing(true);
    startRef.current = {
      x: e.clientX,
      width: displayWidth || imgRef.current?.offsetWidth || 200,
    };
  };

  return (
    <div
      className={cn(
        "relative group inline-flex items-start gap-1",
        isDragging && "opacity-50"
      )}
    >
      <div
        draggable={!readOnly}
        onDragStart={(e) => {
          if (readOnly) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.effectAllowed = "move";
          onDragStart?.();
        }}
        onDragEnd={() => onDragEnd?.()}
        className={cn("text-gray-400 dark:text-gray-500 transition-colors mt-1 flex-shrink-0", readOnly ? "cursor-not-allowed opacity-50" : "cursor-grab active:cursor-grabbing hover:text-gray-600 dark:hover:text-gray-300")}
        title="드래그하여 이동"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div className="relative inline-block">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={previewUrl}
          alt="첨부 이미지"
          style={displayWidth ? { width: `${displayWidth}px` } : undefined}
          className={cn(
            "object-contain rounded-md border border-gray-200 dark:border-neutral-500",
            !displayWidth && "max-w-[200px]",
            resizing && "select-none"
          )}
        />
        <button
          type="button"
          onClick={onRemove}
          disabled={readOnly}
          aria-label="이미지 블록 삭제"
          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
        >
          ×
        </button>
        {/* 리사이즈 핸들 */}
        {!readOnly && (
          <div
            onMouseDown={handleResizeStart}
            className="absolute bottom-1 right-1 w-3 h-3 rounded-sm bg-[#706E6E] cursor-se-resize opacity-0 group-hover:opacity-80 transition-opacity"
            title="드래그하여 크기 조절"
          />
        )}
      </div>
    </div>
  );
}

/** 이미지 삽입 모달 */
function ImageInsertModal({
  onInsert,
  onClose,
  remainingSlots,
  readOnly,
}: {
  onInsert: (files: File[]) => void;
  onClose: () => void;
  remainingSlots: number;
  readOnly: boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      if (readOnly) return;
      const newFiles: File[] = [];
      const newPreviews: string[] = [];
      const slots = remainingSlots - selectedFiles.length;

      for (let i = 0; i < files.length && newFiles.length < slots; i++) {
        const file = files[i];
        const error = validateImageFile(file);
        if (error) {
          alert(error);
          continue;
        }
        newFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }

      if (files.length > slots) {
        alert(`이미지는 최대 ${remainingSlots}개까지 추가할 수 있습니다.`);
      }

      setSelectedFiles((prev) => [...prev, ...newFiles]);
      setPreviews((prev) => [...prev, ...newPreviews]);
    },
    [remainingSlots, selectedFiles.length, readOnly]
  );

  const removeFile = (index: number) => {
    if (readOnly) return;
    URL.revokeObjectURL(previews[index]);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    if (readOnly) return;
    if (selectedFiles.length > 0) {
      onInsert(selectedFiles);
    }
    onClose();
  };

  // 모달 언마운트 시 미사용 preview URL 정리
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 우측 사이드바(aside)는 z-20 + sticky 로 독립 stacking context 를 만들어서, 그 안에서
  // 렌더하면 z-50 이어도 본문의 StickyTitle(z-40)·언어 토글·ContentHeader(z-30)를 못 덮는다.
  // document.body 로 portal 해 stacking context 를 탈출시켜 전체 화면 위에 오게 한다.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
        <h3 className="text-sm font-semibold text-[#333] dark:text-neutral-200 mb-3">
          이미지 삽입
        </h3>

        {/* 드래그 앤 드롭 / 클릭 영역 */}
        {selectedFiles.length < remainingSlots && (
          <div
            role="button"
            tabIndex={readOnly ? -1 : 0}
            aria-disabled={readOnly}
            onKeyDown={(e) => {
              if (!readOnly && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (readOnly) return;
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              addFiles(e.dataTransfer.files);
            }}
            onClick={() => { if (!readOnly) fileInputRef.current?.click(); }}
            className={cn(
              "border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-ring",
              isDragging
                ? "border-[#706E6E] bg-gray-100 dark:bg-gray-700"
                : "border-gray-300 dark:border-neutral-500 hover:border-[#706E6E]",
              readOnly && "cursor-not-allowed opacity-50"
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              disabled={readOnly}
              aria-label="삽입할 이미지 선택"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
              className="hidden"
            />
            <ImagePlus className="h-8 w-8 mx-auto mb-2 text-gray-400 dark:text-neutral-500" />
            <p className="text-sm text-[#706E6E] dark:text-neutral-400">
              이미지를 드래그하거나 클릭하여 선택
            </p>
            <p className="text-xs text-[#706E6E] dark:text-neutral-500 mt-1">
              JPEG, PNG, GIF, WebP / 최대 5MB / {selectedFiles.length}/{remainingSlots}
            </p>
          </div>
        )}

        {/* 선택된 이미지 미리보기 */}
        {previews.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {previews.map((preview, index) => (
              <div key={index} className="relative group">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={preview}
                  alt={`선택 이미지 ${index + 1}`}
                  className="w-20 h-20 object-cover rounded-md border border-gray-200 dark:border-neutral-500"
                />
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  disabled={readOnly}
                  aria-label={`선택 이미지 ${index + 1} 제거`}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 버튼 */}
        <div className="flex gap-2 justify-end mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#706E6E] border border-gray-200 dark:border-neutral-500 rounded-md hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-gray-700 font-light"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={readOnly || selectedFiles.length === 0}
            className="px-4 py-2 text-sm bg-[#706E6E] text-white rounded-md hover:bg-[#5A5858] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            삽입
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface BlockEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  maxContentHeight?: string;
  onImageFilesChange?: (fileMap: Map<string, File>) => void;
  maxImages?: number;
  /** 내용을 유지하고 선택·복사는 허용하면서 편집만 제한한다. */
  readOnly?: boolean;
  /**
   * 부모가 높이를 확정한 flex 컨텍스트(flex-1 min-h-0)에서 에디터가 남은 세로 공간을
   * 모두 채우고, 블록 영역이 내부 스크롤하도록 한다. (UI 2.0 셸 필기 Section·오답노트 셸 패널 용)
   * fill 일 때는 maxContentHeight 대신 부모 높이를 채운다.
   */
  fill?: boolean;
}

export default function BlockEditor({
  value,
  onChange,
  placeholder,
  className,
  maxContentHeight,
  onImageFilesChange,
  maxImages = 3,
  fill,
  readOnly = false,
}: BlockEditorProps) {
  const [blocks, setBlocks] = useState<EditorBlock[]>(() =>
    parseBlocks(value)
  );
  const [showCodeMenu, setShowCodeMenu] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    index: number;
    splitBlockIdx?: number;
    splitCharOffset?: number;
    splitLineY?: number;
  } | null>(null);
  const codeMenuRef = useRef<HTMLDivElement>(null);
  const valueSyncRef = useRef(createBlockEditorValueSyncState(value));
  const focusedBlockRef = useRef<{
    index: number;
    selectionStart: number;
  } | null>(null);

  // 외부 value 변경 시 블록 동기화
  useEffect(() => {
    const receipt = receiveBlockEditorValue(valueSyncRef.current, value);
    valueSyncRef.current = receipt.state;

    if (receipt.source === "external") {
      setBlocks(parseBlocks(value));
    }
  }, [value]);

  // 코드 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        codeMenuRef.current &&
        !codeMenuRef.current.contains(e.target as Node)
      ) {
        setShowCodeMenu(false);
      }
    };
    if (showCodeMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCodeMenu]);

  // blocks 변경 시 부모에 알림 (렌더 중 setState 방지)
  useEffect(() => {
    const newValue = blocksToString(blocks);

    if (newValue !== valueSyncRef.current.currentValue) {
      valueSyncRef.current = recordBlockEditorValueEmission(
        valueSyncRef.current,
        newValue
      );
      onChange(newValue);
    }
  }, [blocks, onChange]);

  // 이미지 파일 맵을 부모에 전달
  useEffect(() => {
    if (!onImageFilesChange) return;
    const fileMap = new Map<string, File>();
    for (const block of blocks) {
      if (block.type === "image" && block.file && block.previewUrl) {
        fileMap.set(block.previewUrl, block.file);
      }
    }
    onImageFilesChange(fileMap);
  }, [blocks, onImageFilesChange]);

  // 컴포넌트 언마운트 시 blob URL 정리
  useEffect(() => {
    return () => {
      for (const block of blocks) {
        if (block.type === "image" && block.file && block.previewUrl) {
          URL.revokeObjectURL(block.previewUrl);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateBlock = useCallback(
    (index: number, newContent: string) => {
      if (readOnly) return;
      setBlocks((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], content: newContent };
        return updated;
      });
    },
    [readOnly]
  );

  const removeBlock = useCallback(
    (index: number) => {
      if (readOnly) return;
      setBlocks((prev) => {
        const removedBlock = prev[index];
        // 이미지 블록 제거 시 blob URL 정리
        if (removedBlock.type === "image" && removedBlock.file && removedBlock.previewUrl) {
          URL.revokeObjectURL(removedBlock.previewUrl);
        }

        const updated = prev.filter((_, i) => i !== index);
        // 인접한 텍스트 블록 병합
        const merged: EditorBlock[] = [];
        for (const block of updated) {
          if (
            block.type === "text" &&
            merged.length > 0 &&
            merged[merged.length - 1].type === "text"
          ) {
            merged[merged.length - 1] = {
              ...merged[merged.length - 1],
              content:
                merged[merged.length - 1].content + "\n" + block.content,
            };
          } else {
            merged.push({ ...block });
          }
        }
        if (merged.length === 0) {
          merged.push({ id: genId(), type: "text", content: "" });
        }
        return merged;
      });
    },
    [readOnly]
  );

  const insertCodeBlock = useCallback(
    (language: string) => {
      if (readOnly) return;
      setBlocks((prev) => {
        const focused = focusedBlockRef.current;

        if (focused && prev[focused.index]?.type === "text") {
          const block = prev[focused.index];
          const before = block.content.slice(0, focused.selectionStart).replace(/\n+$/, "");
          const after = block.content.slice(focused.selectionStart).replace(/^\n+/, "");

          const updated = [...prev];
          updated.splice(
            focused.index,
            1,
            { id: block.id, type: "text", content: before },
            { id: genId(), type: "code", content: "", language },
            { id: genId(), type: "text", content: after }
          );
          return updated;
        }

        // 포커스된 텍스트 블록이 없으면 맨 뒤에 추가
        const updated = [...prev];
        updated.push(
          { id: genId(), type: "code", content: "", language },
          { id: genId(), type: "text", content: "" }
        );
        return updated;
      });
      setShowCodeMenu(false);
    },
    [readOnly]
  );

  const insertImageBlock = useCallback(
    (file: File) => {
      if (readOnly) return;
      const error = validateImageFile(file);
      if (error) {
        alert(error);
        return;
      }

      const previewUrl = URL.createObjectURL(file);

      setBlocks((prev) => {
        const currentImageCount = prev.filter((b) => b.type === "image").length;
        if (currentImageCount >= maxImages) {
          URL.revokeObjectURL(previewUrl);
          alert(`이미지는 최대 ${maxImages}개까지 첨부할 수 있습니다.`);
          return prev;
        }

        const focused = focusedBlockRef.current;

        if (focused && prev[focused.index]?.type === "text") {
          const block = prev[focused.index];
          const before = block.content.slice(0, focused.selectionStart).replace(/\n+$/, "");
          const after = block.content.slice(focused.selectionStart).replace(/^\n+/, "");

          const updated = [...prev];
          updated.splice(
            focused.index,
            1,
            { id: block.id, type: "text", content: before },
            { id: genId(), type: "image", content: previewUrl, file, previewUrl },
            { id: genId(), type: "text", content: after }
          );
          return updated;
        }

        // 포커스된 텍스트 블록이 없으면 맨 뒤에 추가
        const updated = [...prev];
        updated.push(
          { id: genId(), type: "image", content: previewUrl, file, previewUrl },
          { id: genId(), type: "text", content: "" }
        );
        return updated;
      });
    },
    [maxImages, readOnly]
  );

  // 클립보드 붙여넣기 핸들러
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (readOnly) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file" && items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length === 0) return; // 텍스트 붙여넣기는 기본 동작 유지

      e.preventDefault();

      for (const file of imageFiles) {
        insertImageBlock(file);
      }
    },
    [insertImageBlock, readOnly]
  );

  // 이미지 모달에서 파일 삽입 핸들러
  const handleImageInsertFromModal = useCallback(
    (files: File[]) => {
      for (const file of files) {
        insertImageBlock(file);
      }
    },
    [insertImageBlock]
  );

  const moveBlock = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (readOnly) return;
      setBlocks((prev) => {
        if (toIndex === fromIndex || toIndex === fromIndex + 1) return prev;

        const block = prev[fromIndex];
        const updated = prev.filter((_, i) => i !== fromIndex);
        const insertAt = toIndex > fromIndex ? toIndex - 1 : toIndex;
        updated.splice(insertAt, 0, block);

        // 인접한 텍스트 블록 병합
        const merged: EditorBlock[] = [];
        for (const b of updated) {
          if (
            b.type === "text" &&
            merged.length > 0 &&
            merged[merged.length - 1].type === "text"
          ) {
            merged[merged.length - 1] = {
              ...merged[merged.length - 1],
              content:
                merged[merged.length - 1].content + "\n" + b.content,
            };
          } else {
            merged.push({ ...b });
          }
        }

        // 비텍스트 블록 주변에 텍스트 블록 보장
        const result: EditorBlock[] = [];
        for (let i = 0; i < merged.length; i++) {
          if (
            merged[i].type !== "text" &&
            (result.length === 0 ||
              result[result.length - 1].type !== "text")
          ) {
            result.push({ id: genId(), type: "text", content: "" });
          }
          result.push(merged[i]);
        }
        if (result[result.length - 1].type !== "text") {
          result.push({ id: genId(), type: "text", content: "" });
        }

        return result;
      });
      setDraggedIndex(null);
      setDropTarget(null);
    },
    [readOnly]
  );

  /** 컨테이너 레벨 dragOver: 마우스 Y 좌표로 블록/줄 단위 드롭 위치 계산 */
  const handleContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      if (readOnly) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "none";
        return;
      }
      if (draggedIndex === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      const container = e.currentTarget;
      const mouseY = e.clientY;
      const blockEls = container.querySelectorAll<HTMLElement>("[data-block-idx]");

      let best: { index: number; splitBlockIdx?: number; splitCharOffset?: number; splitLineY?: number } = { index: blocks.length };
      let minDist = Infinity;

      blockEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const idx = parseInt(el.dataset.blockIdx!);

        // 블록 상단/하단 경계
        const topDist = Math.abs(mouseY - rect.top);
        if (topDist < minDist) {
          minDist = topDist;
          best = { index: idx };
        }
        const bottomDist = Math.abs(mouseY - rect.bottom);
        if (bottomDist < minDist) {
          minDist = bottomDist;
          best = { index: idx + 1 };
        }

        // 텍스트 블록: 줄 경계도 확인
        const block = blocks[idx];
        if (block?.type === "text" && block.content.includes("\n")) {
          const textarea = el.querySelector("textarea");
          if (!textarea) return;

          const taRect = textarea.getBoundingClientRect();
          const style = getComputedStyle(textarea);
          const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.625;
          const paddingTop = parseFloat(style.paddingTop) || 0;

          const lines = block.content.split("\n");
          let charOffset = 0;
          for (let li = 1; li < lines.length; li++) {
            charOffset += lines[li - 1].length + 1;
            const lineY = taRect.top + paddingTop + li * lineHeight;
            const dist = Math.abs(mouseY - lineY);
            if (dist < minDist) {
              minDist = dist;
              best = {
                index: idx,
                splitBlockIdx: idx,
                splitCharOffset: charOffset,
                splitLineY: paddingTop + li * lineHeight,
              };
            }
          }
        }
      });

      setDropTarget(best);
    },
    [draggedIndex, blocks, readOnly]
  );

  const handleContainerDrop = useCallback(
    (e: React.DragEvent) => {
      if (readOnly) {
        e.preventDefault();
        return;
      }
      if (draggedIndex === null || !dropTarget) return;
      e.preventDefault();

      if (dropTarget.splitBlockIdx != null && dropTarget.splitCharOffset != null) {
        // 텍스트 블록을 줄 경계에서 분할 후 드래그된 블록 삽입
        setBlocks((prev) => {
          const splitIdx = dropTarget.splitBlockIdx!;
          const textBlock = prev[splitIdx];
          if (textBlock.type !== "text") return prev;

          const beforeText = textBlock.content.slice(0, dropTarget.splitCharOffset!).replace(/\n+$/, "");
          const afterText = textBlock.content.slice(dropTarget.splitCharOffset!).replace(/^\n+/, "");

          // 분할된 블록 배열 생성
          const withSplit: EditorBlock[] = [];
          for (let i = 0; i < prev.length; i++) {
            if (i === splitIdx) {
              withSplit.push({ ...textBlock, content: beforeText });
              withSplit.push({ id: genId(), type: "text", content: afterText });
            } else {
              withSplit.push(prev[i]);
            }
          }

          // 드래그된 블록의 인덱스 조정 (분할로 인해 밀림)
          const adjDragIdx = draggedIndex > splitIdx ? draggedIndex + 1 : draggedIndex;
          const draggedBlock = withSplit[adjDragIdx];

          // 드래그된 블록 제거
          const without = withSplit.filter((_, i) => i !== adjDragIdx);

          // 삽입 위치 계산 (분할 지점 사이)
          const insertAt = adjDragIdx <= splitIdx ? splitIdx : splitIdx + 1;
          without.splice(insertAt, 0, draggedBlock);

          // 인접 텍스트 블록 병합
          const merged: EditorBlock[] = [];
          for (const b of without) {
            if (b.type === "text" && merged.length > 0 && merged[merged.length - 1].type === "text") {
              merged[merged.length - 1] = {
                ...merged[merged.length - 1],
                content: merged[merged.length - 1].content + "\n" + b.content,
              };
            } else {
              merged.push({ ...b });
            }
          }

          // 비텍스트 블록 주변에 텍스트 블록 보장
          const result: EditorBlock[] = [];
          for (let i = 0; i < merged.length; i++) {
            if (merged[i].type !== "text" && (result.length === 0 || result[result.length - 1].type !== "text")) {
              result.push({ id: genId(), type: "text", content: "" });
            }
            result.push(merged[i]);
          }
          if (result.length > 0 && result[result.length - 1].type !== "text") {
            result.push({ id: genId(), type: "text", content: "" });
          }

          return result.length > 0 ? result : [{ id: genId(), type: "text", content: "" }];
        });

        setDraggedIndex(null);
        setDropTarget(null);
      } else {
        moveBlock(draggedIndex, dropTarget.index);
        setDropTarget(null);
      }
    },
    [draggedIndex, dropTarget, moveBlock, readOnly]
  );

  return (
    <div
      className={cn(
        "rounded-md border-[0.5px] border-gray-200 dark:border-neutral-400",
        "bg-white dark:bg-gray-700",
        "focus-within:ring-2 focus-within:ring-[#706E6E] dark:focus-within:ring-neutral-400",
        // fill: 부모(flex-1 min-h-0)의 남은 높이를 채우고 블록 영역이 내부 스크롤
        fill && "flex min-h-0 flex-1 flex-col overflow-hidden",
        className
      )}
    >
      {/* 툴바 — 내용이 길어 스크롤돼도 에디터 상단에 계속 보이도록 sticky 고정 (전역, JCG-2156).
          부모/내부 스크롤 컨텍스트(fill·maxContentHeight·셸 패널) 모두에서 동작하며,
          스크롤되는 블록을 덮도록 컨테이너 배경(bg-inherit)을 따라 불투명하게 두고 z-index를 둔다.
          bg-inherit 이라 caller 가 bg-muted 등으로 컨테이너 배경을 바꿔도 툴바가 그대로 맞춰진다. */}
      <div className="sticky top-0 z-30 flex shrink-0 items-center gap-1 px-2 py-1 border-b border-gray-100 dark:border-neutral-600 bg-inherit rounded-t-md">
        <div className="relative" ref={codeMenuRef}>
          <button
            type="button"
            onClick={() => { if (!readOnly) setShowCodeMenu(!showCodeMenu); }}
            disabled={readOnly}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[#706E6E] dark:text-neutral-400 hover:text-[#333] dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            title="코드 블록 삽입"
            aria-haspopup="menu"
            aria-expanded={showCodeMenu && !readOnly}
          >
            <Code className="h-3.5 w-3.5" aria-hidden="true" />
            <span>코드</span>
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          </button>
          {showCodeMenu && !readOnly && (
            <div
              role="menu"
              className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-neutral-600 rounded-md shadow-lg z-10 min-w-[120px]"
            >
              {CODE_LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  role="menuitem"
                  onClick={() => insertCodeBlock(lang.value)}
                  className="w-full text-left px-3 py-2 text-sm text-[#333] dark:text-neutral-200 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-md last:rounded-b-md transition-colors"
                >
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => { if (!readOnly) setShowImageModal(true); }}
          disabled={readOnly}
          className="flex items-center gap-1 px-2 py-1 text-xs text-[#706E6E] dark:text-neutral-400 hover:text-[#333] dark:hover:text-neutral-200 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          title="이미지 삽입"
        >
          <ImagePlus className="h-3.5 w-3.5" aria-hidden="true" />
          <span>이미지</span>
        </button>
      </div>

      {/* 블록 영역 — fill 이면 남은 높이를 채우고 내부 스크롤, 아니면 maxContentHeight 로 cap */}
      <div
        className={cn(
          "px-3 py-2 overflow-y-auto",
          fill && "min-h-0 flex-1",
        )}
        style={!fill && maxContentHeight ? { maxHeight: maxContentHeight } : undefined}
        onPaste={handlePaste}
        onMouseDown={(e) => {
          // 블록 영역의 빈 공간(컨테이너 자체)을 누르면 마지막 텍스트 블록으로 포커스 + 커서 끝으로.
          // 블록(자식)을 직접 누른 경우엔 기본 동작을 그대로 둔다.
          if (e.target !== e.currentTarget) return;
          const textareas = e.currentTarget.querySelectorAll<HTMLTextAreaElement>(
            "textarea[data-text-block]",
          );
          const last = textareas[textareas.length - 1];
          if (last) {
            e.preventDefault();
            last.focus();
            const end = last.value.length;
            last.setSelectionRange(end, end);
          }
        }}
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
      >
        {blocks.map((block, i) => (
          <Fragment key={block.id}>
            {/* 블록 경계 드롭 인디케이터 */}
            {!readOnly && draggedIndex !== null && dropTarget?.index === i && !dropTarget.splitBlockIdx && (
              <div className="h-0.5 bg-blue-500 rounded-full" />
            )}
            <div data-block-idx={i} className="relative">
              {/* 텍스트 블록 내 줄 경계 드롭 인디케이터 */}
              {!readOnly && draggedIndex !== null && dropTarget?.splitBlockIdx === i && dropTarget.splitLineY != null && (
                <div
                  className="absolute left-0 right-0 h-0.5 bg-blue-500 rounded-full pointer-events-none z-20"
                  style={{ top: `${dropTarget.splitLineY}px` }}
                />
              )}
              {block.type === "text" ? (
                <AutoResizeTextarea
                  value={block.content}
                  readOnly={readOnly}
                  onChange={(val) => updateBlock(i, val)}
                  placeholder={
                    i === 0 && blocks.length === 1 ? placeholder : undefined
                  }
                  onFocusBlock={() => {
                    focusedBlockRef.current = {
                      index: i,
                      selectionStart: 0,
                    };
                  }}
                  onCursorChange={(pos) => {
                    focusedBlockRef.current = {
                      index: i,
                      selectionStart: pos,
                    };
                  }}
                />
              ) : block.type === "code" ? (
                <CodeBlockEdit
                  value={block.content}
                  readOnly={readOnly}
                  language={block.language || "text"}
                  onChange={(val) => updateBlock(i, val)}
                  onRemove={() => removeBlock(i)}
                  isDragging={!readOnly && draggedIndex === i}
                  onDragStart={() => setDraggedIndex(i)}
                  onDragEnd={() => {
                    setDraggedIndex(null);
                    setDropTarget(null);
                  }}
                />
              ) : (
                <ImageBlockEdit
                  previewUrl={block.previewUrl || block.content}
                  readOnly={readOnly}
                  imageWidth={block.imageWidth}
                  onWidthChange={(w) => {
                    if (readOnly) return;
                    setBlocks((prev) => {
                      const updated = [...prev];
                      updated[i] = { ...updated[i], imageWidth: w };
                      return updated;
                    });
                  }}
                  onRemove={() => removeBlock(i)}
                  isDragging={!readOnly && draggedIndex === i}
                  onDragStart={() => setDraggedIndex(i)}
                  onDragEnd={() => {
                    setDraggedIndex(null);
                    setDropTarget(null);
                  }}
                />
              )}
            </div>
          </Fragment>
        ))}
        {/* 맨 끝 드롭 인디케이터 */}
        {!readOnly && draggedIndex !== null && dropTarget?.index === blocks.length && !dropTarget.splitBlockIdx && (
          <div className="h-0.5 bg-blue-500 rounded-full" />
        )}
      </div>

      {/* 이미지 삽입 모달 */}
      {showImageModal && (
        <ImageInsertModal
          readOnly={readOnly}
          remainingSlots={maxImages - blocks.filter((b) => b.type === "image").length}
          onInsert={handleImageInsertFromModal}
          onClose={() => setShowImageModal(false)}
        />
      )}
    </div>
  );
}
